import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import * as Sentry from '@sentry/react';
import { theme } from './lib/mui/theme';
import { queryClient } from './lib/query/client';
import { ClientProvider } from './contexts/ClientContext';
import { NotificationProvider } from './contexts/NotificationContext';

import { SignalRProvider } from './contexts/SignalRContext';
import { SentryErrorFallback } from './components/errors/SentryErrorFallback';
import { SentryUserContext } from './components/errors/SentryUserContext';
import type { QueryClient } from '@tanstack/react-query';
import { setupClientContextInterceptor } from './lib/api/interceptors/clientContextInterceptor';
import { setupClientAuthErrorInterceptor } from './lib/api/interceptors/clientAuthErrorInterceptor';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Initialize client switching interceptors
// These must be set up before any API calls are made
setupClientContextInterceptor();
setupClientAuthErrorInterceptor();

// Define router context type
export interface RouterContext {
  queryClient: QueryClient;
}

// Create a new router instance with typed context and prefetching optimizations
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  } as RouterContext,
  // Optimize for external cache integration (TanStack Query)
  defaultPreloadStaleTime: 0, // Always run loaders for external cache deduplication
  defaultPreload: 'intent', // Preload on hover/focus
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
          <SignalRProvider>
            <ClientProvider>
              <SentryUserContext />
              <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>
                <RouterProvider router={router} />
              </Sentry.ErrorBoundary>
            </ClientProvider>
          </SignalRProvider>
        </NotificationProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
